import React from 'react';
import * as yup from 'yup';

import Button from '@hero/ui-kit/inputs/Button';
import Checkbox from '@hero/ui-kit/inputs/Checkbox';
import Form from '@hero/ui-kit/inputs/Form';
import Input from '@hero/ui-kit/inputs/Input';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';

import { useCareAction } from '../api/useCareWrites';
import { CODE_ACTION_LABELS, careErrorText, noteActionLabel } from '../constants';
import { DecisionValues, decisionPayload } from '../decisionPayload';
import { patientLabel } from '../rowSets';
import { rowExplanation, rowNote, warningExplanation } from '../presentation';
import * as Style from '../style.module.scss';
import { ActionTarget, DecisionOutcome } from '../types';

type Props = {
    month: string;
    target: ActionTarget;
    dependentCodes: string[];
    baseCode?: string;
    closeModal: () => void;
    canWrite: boolean;
    onCalculationResult: (error?: string) => void;
};

const ActionForm: React.FC<Props> = ({ month, target, dependentCodes, baseCode, closeModal, canWrite, onCalculationResult }) => {
    const { row, action: descriptor } = target;
    const name = descriptor.action;
    const label =
        name === 'note'
            ? noteActionLabel(rowNote(row))
            : CODE_ACTION_LABELS[name];
    const mutation = useCareAction(month, `${row.program} ${row.code} saved.`, onCalculationResult);
    const [outcome, setOutcome] = React.useState<DecisionOutcome | null>(null);
    const [localError, setLocalError] = React.useState<string>();
    const [submitted, setSubmitted] = React.useState(false);
    const condition = descriptor?.condition_confirmation;
    const supportsNote = name !== 'hold' && name !== 'clear_hold';
    const needsReference =
        outcome !== null && descriptor?.reference_required_outcomes?.includes(outcome);
    const needsApproval =
        outcome !== null && descriptor.approval_reference_required_outcomes?.includes(outcome);
    const schema = yup.object({
        reason:
            (name !== 'note' && descriptor.reason_required)
                ? yup.string().trim().required('Enter a reason.')
                : yup.string(),
        note: name === 'note' ? yup.string().trim().required('Enter a note.') : yup.string(),
        approval_reference: needsApproval
            ? yup.string().trim().required('Enter the approval reference.')
            : yup.string(),
        reference: needsReference
            ? yup
                  .string()
                  .trim()
                  .matches(
                      /^[A-Za-z0-9_.:-]{1,128}$/,
                      'Use a record reference (letters, numbers, -, _, . or :).'
                  )
                  .required('Enter a documentary reference.')
            : yup.string(),
        statement_confirmed: needsReference
            ? yup
                  .boolean()
                  .oneOf([true], 'Confirm the clinical statement.')
                  .required('Confirm the clinical statement.')
            : yup.boolean()
    });

    const handleSubmit = async (values: DecisionValues) => {
        if (!canWrite || submitted || mutation.isPending) return;
        setLocalError(undefined);
        try {
            const payload = decisionPayload(target, values, outcome);
            setSubmitted(true);
            await mutation.mutateAsync(payload);
            closeModal();
        } catch (error) {
            setLocalError(error instanceof Error ? error.message : careErrorText(error));
            // Tokens belong to the evidence the reviewer saw. Never silently replace and resubmit them.
            setSubmitted(true);
        }
    };

    return (
        <Section>
            <H role="h5">{`${label}: ${patientLabel(row)}, ${row.program} ${row.code}`}</H>

            {name === 'hold' ? (
                <P>
                    Block billing until you unblock it. Unresolved warnings will still need a
                    decision.
                </P>
            ) : null}
            {name === 'clear_hold' ? (
                <P>
                    Unblocking restores the calculated result. Unresolved warnings still need a
                    decision.
                </P>
            ) : null}
            <P noDefaultMargin>{`Service month: ${month}`}</P>
            <P>{rowExplanation(row, month, baseCode)}</P>
            {descriptor?.warning_kinds
                ?.filter((kind) => kind !== (row.operator_hold || row.result_reason || row.reason))
                .map((kind) => (
                    <P key={kind} strong>
                        {warningExplanation(row, kind, month, baseCode)}
                    </P>
                ))}
            <P>This action applies only to {row.program} {row.code} for {month}.</P>
            {name !== 'note' && dependentCodes.length ? (
                <P size="small">
                    {dependentCodes.join(', ')} also needs this first code to be billable. Its
                    review decisions remain separate.
                </P>
            ) : null}
            {name !== 'note' && row.current_evidence.facts?.dependency ? (
                <P size="small">
                    This additional code also needs a billable first code. Each code has its own
                    review decision.
                </P>
            ) : null}
            {name === 'resolve_warning' ? (
                <div className={Style.outcomeControls} role="group" aria-label="Decision outcome">
                    {descriptor?.allowed_outcomes?.map((value) => (
                        <Button
                            key={value}
                            size="small"
                            variant={outcome === value ? 'primary' : 'outlineSecondary'}
                            width="natural"
                            noDefaultMargin
                            disabled={submitted || !canWrite}
                            aria-pressed={outcome === value}
                            onClick={() => setOutcome(value)}
                        >
                            {value === 'billable' ? 'Billable' : 'Not billable'}
                        </Button>
                    ))}
                </div>
            ) : null}
            <Form
                validationSchema={schema}
                submitFn={handleSubmit}
                validationMode="onTouched"
                defaultValues={{ note: name === 'note' ? rowNote(row) : '' }}
            >
                {name !== 'note' && (
                    <TextArea
                        name="reason"
                        displayName="Reason"
                        disabled={submitted || !canWrite}
                    />
                )}
                {needsApproval && (
                    <Input
                        name="approval_reference"
                        displayName="Approval reference"
                        disabled={submitted || !canWrite}
                    />
                )}
                {needsReference && condition ? (
                    <>
                        <P size="small">
                            Documentary reference only; do not paste clinical records.
                        </P>
                        <Input
                            name="reference"
                            displayName="Record reference"
                            disabled={submitted || !canWrite}
                        />
                        <Checkbox
                            name="statement_confirmed"
                            labelText={condition.statement}
                            disabled={submitted || !canWrite}
                        />
                    </>
                ) : null}
                {supportsNote ? (
                    <TextArea
                        name="note"
                        displayName={name === 'note' ? 'Note' : 'Note (optional)'}
                        disabled={submitted || !canWrite}
                    />
                ) : null}
                {localError ? (
                    <P className={Style.apiError} role="alert">
                        {localError} Close this form, review the refreshed row and reopen the
                        action.
                    </P>
                ) : null}
                <Section noDefaultPadding paddingTop="small" centered>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={
                            !canWrite ||
                            submitted ||
                            mutation.isPending ||
                            (name === 'resolve_warning' && !outcome)
                        }
                    >
                        {mutation.isPending ? 'Saving and updating…' : label}
                    </Button>
                    {submitted && localError ? (
                        <Button variant="outlineSecondary" onClick={closeModal}>
                            Close and review
                        </Button>
                    ) : null}
                </Section>
            </Form>
        </Section>
    );
};

export default ActionForm;
